import { COLUMNS } from '../utils/claim-columns';
import type { ClaimRow, SortState, SortableColumn } from '../types/claims.types';
import { ClaimRowGroup } from './ClaimRowGroup';
import { ColumnHeader } from './ClaimsGridHeader';

interface ClaimsGridProps {
  readonly rows: readonly ClaimRow[];
  readonly sort: SortState | null;
  readonly selectedClaimNumber: string | null;
  /** The lines of a claim to list when it is expanded: the ones that match the current view. */
  linesOf: (claim: ClaimRow) => readonly ClaimRow[];
  onToggleSort: (column: SortableColumn) => void;
  onSelect: (claim: ClaimRow) => void;
}

/**
 * The claims table (US-009 AC-003). Rows expand in place to show line entries, and the claim number
 * opens the detail panel without leaving the grid (US-012).
 */
export function ClaimsGrid({
  rows,
  sort,
  selectedClaimNumber,
  linesOf,
  onToggleSort,
  onSelect,
}: ClaimsGridProps): React.JSX.Element {
  return (
    // A focusable scroll container is the documented pattern for a wide table: without it the columns
    // past the fold are unreachable without a pointer (WCAG 2.1.1). The rule assumes tabIndex on a
    // non-widget element is a mistake; here the element is the scrollable region itself.
    <div
      role="region"
      aria-label="Claims table"
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
      tabIndex={0}
      className="focus-visible:outline-brand-600 overflow-x-auto focus-visible:outline-2"
    >
      <table className="w-full min-w-[1100px] border-collapse text-left text-[13px]">
        <caption className="sr-only">Claims for the searched policy</caption>
        <thead>
          <tr className="border-line border-b bg-surface-muted">
            <th scope="col" className="w-8 px-2 py-2">
              <span className="sr-only">Expand claim lines</span>
            </th>
            {COLUMNS.map((column) => (
              <ColumnHeader key={column.key} column={column} sort={sort} onToggleSort={onToggleSort} />
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((claim) => (
            <ClaimRowGroup
              key={claim.claimNumber}
              claim={claim}
              lines={linesOf(claim)}
              selected={selectedClaimNumber === claim.claimNumber}
              onSelect={onSelect}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
